import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = process.env.REACT_APP_GA_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;

// ─── Google Analytics (gtag.js) ────────────────────────────────────────
let gaInstalled = false;
function installGtag() {
  if (gaInstalled || !GA_ID || typeof window === "undefined") return;
  gaInstalled = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // send_page_view:false — React Router pageviews are handled below
  window.gtag("config", GA_ID, { send_page_view: false });
}

// ─── Microsoft Clarity ─────────────────────────────────────────────────
// Session recordings + heatmaps. Free, unlimited sessions.
// Activate by setting REACT_APP_CLARITY_ID=xxxxxxxxxx in frontend/.env.
let clarityInstalled = false;
function installClarity() {
  if (clarityInstalled || !CLARITY_ID || typeof window === "undefined") return;
  clarityInstalled = true;

  // Exact snippet from Microsoft Clarity, translated into JS literals
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_ID);
}

/**
 * Drop this once at the root of <App/>. It:
 *   - installs gtag.js + Microsoft Clarity on first render
 *   - fires a `page_view` event on every React Router navigation
 *
 * To activate analytics, set in frontend/.env:
 *   REACT_APP_GA_ID=G-XXXXXXXXXX
 *   REACT_APP_CLARITY_ID=xxxxxxxxxx
 */
export default function Analytics() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    installGtag();
    installClarity();
  }, []);

  useEffect(() => {
    if (GA_ID && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: pathname + search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    // Clarity automatically tracks SPA navigations via History API, but a
    // friendly `set` tag helps segment recordings by route inside the
    // Clarity dashboard.
    if (CLARITY_ID && typeof window.clarity === "function") {
      window.clarity("set", "page_path", pathname);
    }
  }, [pathname, search]);

  return null;
}

// ─── Helpers ────────────────────────────────────────────────────────────
// Custom event for GA (e.g. studio_checkout_start, free_download)
export function trackEvent(name, params = {}) {
  if (GA_ID && typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", name, params);
  }
  // Mirror to Clarity as a custom tag — shows up in session filters
  if (CLARITY_ID && typeof window !== "undefined" && typeof window.clarity === "function") {
    window.clarity("event", name);
  }
}

/**
 * Call this after a user signs in (Library or Portal) so Clarity sessions
 * can be filtered by buyer email. GA already infers user via gtag config.
 * Safe to call repeatedly — Clarity dedupes internally.
 */
export function identifyUser({ email, role } = {}) {
  if (!email) return;
  if (CLARITY_ID && typeof window !== "undefined" && typeof window.clarity === "function") {
    // Custom-id (hashed by Clarity), plus a friendly session label
    window.clarity("identify", email, undefined, undefined, email);
    if (role) window.clarity("set", "role", role);
  }
  if (GA_ID && typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("set", "user_properties", { user_role: role || "visitor" });
  }
}
